import { useTranslation } from "react-i18next";
import { EditPolicy, MatchedPolicyWithCommission, Policy } from "../../models/Policies";
import { set, SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { AppDispatch, RootState } from "../../state/store";
import { useDispatch, useSelector } from "react-redux";
import { Fragment, useEffect, useState } from "react";
import { AgentCommissionRule } from "../../models/AgentCommissionRule";
import { toast, ToastContainer } from "react-toastify";
import { Modal } from "../../components/Modal";
import { DocumentPlusIcon, PaperAirplaneIcon, PencilIcon } from "@heroicons/react/24/outline";
import { ReactiveFormInput } from "../../components/form/ReactiveFormInput";
import { ReactiveFormSelect } from "../../components/form/ReactiveFormSelect";
import { getPolicyStates } from "../../enums/PolicyState";
import { ReactiveFormRadioButtonSelect } from "../../components/form/ReactiveFormRadioButtonSelect";
import { RoleEnum } from "../../enums/Role";
import { User } from "../../models/User";
import { fetchAgencyUserCommissionRules } from "../../state/agencies/action";
import { fetchPoliciesByClientId, updatePolicy } from "../../state/policies/actions";
import { Toast } from "../../components/Toast";
import { LoadingMask } from "../../components/LoadingMask";
import { ReactiveFormSearchableSelect } from "../../components/form/ReactiveFormSearchableSelect";
import { ReactiveFormRemoteSearchableSelect } from "../../components/form/ReactiveFormRemoteSearchableSelect";
import { fetchClientById, searchClients } from "../../state/clients/actions";
import { Client } from "../../models/Client";
import { createMatchedPolicy } from "../../state/commissions/action";
import { ReactiveFormCheckbox } from "../../components/form/ReactiveFormCheckbox";
import { ConfirmationModal } from "../../components/ConfirmationModal";
import { SpinCircle } from "../../components/SpinCircle";
import moment from "moment";
import { AgencyPolicyType, PolicyMarket, PolicyType } from "../../models/PolicyMarket";
import { PolciyMarketEnum } from "../../enums/PolicyMarket";
import { PolciyTypeEnum } from "../../enums/PolicyType";
import { getEnrollmentMethods } from "../../enums/EnrollmentMethod";

interface Props {
  info: {
    show: boolean;
    policy?: any;
  };
  onClose: () => void;
}

export const ReviewUnmatchedPolicyModal: React.FC<Props> = ({ info, onClose }) => {
  const { t } = useTranslation();
  const [isPolicyUpdateLoading, setIsPolicyUpdateLoading] = useState(false);
  const agencyCarriers = useSelector((state: RootState) => state.carriers.agencyCarriers);

  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector((state: any) => state.users.currentUser);
  const agents = useSelector((state: any) => state.users.users);
  const [selectedPossibleClient, setSelectedPossibleClient] = useState<any | null>(null);
  const [loadingPossibleClient, setLoadingPossibleClient] = useState(false);
  const [loadingPolicies, setLoadingPolicies] = useState(false);

  const [commissionRules, setCommissionRules] = useState<AgentCommissionRule[]>(
    []
  );
  const [selectedCommissionRule, setSelectedCommissionRule] = useState<
    AgentCommissionRule | undefined
  >(undefined);
  const [loadingRules, setLoadingRules] = useState(false);

  const [selectedClientPolicies, setSelectedClientPolicies] = useState<Policy[]>([]);
  const agentAvailableMarkets = useSelector((state: RootState) => state.policyMarkets.agencyPolicyTypes);
  const [selectedPolicyMarket, setSelectedPolicyMarket] =useState<PolicyMarket>();
  const [selectedPolicyType, setSelectedPolicyType] = useState<PolicyType>();
  const [policyTypes, setPolicyTypes] = useState<PolicyType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  const methods = useForm<MatchedPolicyWithCommission>({
    defaultValues: {}
  });
  const { handleSubmit, control, setValue, getValues, reset } = methods;

  useEffect(() => {
    info.policy ? reset(info.policy) : reset({});

    if (info.policy && info.policy.possible_agent_id) {
      if (agents.find((x: any) => x.id === info.policy!.possible_agent_id)) {
        methods.setValue("agent_id", info.policy.possible_agent_id);
        loadCommissionRules(+info.policy.possible_agent_id!);
      } else {
        info.policy.agent_id = undefined;
      }
    }

    if (info.policy && info.policy.possible_client_id) {
      setLoadingPossibleClient(true);
      dispatch(fetchClientById(info.policy.possible_client_id))
      .then((e: any) => setSelectedPossibleClient(
        { label: `${e.payload.first_name} ${e.payload.middle_name ?? ""} ${e.payload.last_name} ${e.payload.second_last_name ?? ""}`.trim().replaceAll("  ", " "), 
        value: e.payload.id }))
      .finally(() => setLoadingPossibleClient(false));
    }
  }, [info.policy, reset]);

  useEffect(() => {
    methods.setValue("client_id", selectedPossibleClient?.value);

    if (selectedPossibleClient) {
      setLoadingPolicies(true);
      dispatch(
        fetchPoliciesByClientId({
          clientId: selectedPossibleClient.value,
        })
      ).then((e) => {
        setSelectedClientPolicies((e.payload as Policy[])
          .filter((x: Policy) => x.number.trim() === "" && x.carrier_id === +methods.getValues("carrier_id") && x.agency_id === currentUser.agency_id));

      }).catch(() => setSelectedClientPolicies([]))
      .finally(() => setLoadingPolicies(false));

    }
  }, [selectedPossibleClient]);
  
  const loadCommissionRules = (agentId: number) => {
    setLoadingRules(true);
        dispatch(
          fetchAgencyUserCommissionRules({
            agencyId: currentUser.agency_id,
            userId: agentId,
          })
        ).then((e) => {
          let rules = (e.payload || []) as AgentCommissionRule[];
          setCommissionRules(rules);

          let selectedRule = rules.find((x: AgentCommissionRule) => x.is_default);

          setSelectedCommissionRule(selectedRule);
          methods.setValue("agent_commission_rule_id", selectedRule?.id);
          setLoadingRules(false);
        });
  };

  const setObamacareMembersInsured = (policyType?: PolicyType) => {
    if(policyType && policyType.id === PolciyTypeEnum.OBAMACARE) {
      if(info.policy) {
        setValue("additional_information.members_insured", info.policy.members_insured)
      }
    }
  }

  const onSubmitInvalid: SubmitErrorHandler<EditPolicy> = (data) => {
    toast(t("PLEASE_ENTER_ALL_POLICY_REQUIRED_FIELDS"));
  };

  const onSubmit: SubmitHandler<any> = async (data: any) => {
    if (checkPolicyMembers(data)) {
      Toast(t("POLICY_MEMBERS_ERROR"), t("POLICY_MEMBERS_MESSAGE"));
    } else {
      const payload: MatchedPolicyWithCommission = {
        id: +data.id,
        carrier_id: +data.carrier_id,
        client_id: +data.client_id,
        policy_number: data.policy_number,
        effective_start_date: data.effective_start_date,
        effective_end_date: data.effective_end_date,
        policy_state: data.policy_state,
        agent_id: +data.agent_id,
        agent_commission_rule_id: +data.agent_commission_rule_id,
        commission: +data.total_income,
        billing_date: data.billing_date,
        statement_date: data.statement_date,
        policy_id_to_override: data.policy_id_to_override === "-1" || isNaN(+data.policy_id_to_override) ? undefined : +data.policy_id_to_override,
        sold_date: data.sold_date,
        policy_market_id: +data.policy_market_id,
        policy_type_id: +data.policy_type_id,
        additional_information: data.additional_information
      };

      setIsPolicyUpdateLoading(true);

      dispatch(createMatchedPolicy({ body: payload, agencyId: currentUser.agency_id })).then((e) => {
        if (e.type === "commissions/createMatchedPolicy/rejected") {
          let error = e.payload as any;
          Toast(
            t(error.reason ?? "SOMETHING_WENT_WRONG"),
            t(error.cause_info)
          );
          setIsPolicyUpdateLoading(false);
        } else {
          toast(t("SAVED_SUCCESFULLY"));
          setIsPolicyUpdateLoading(false);
          closeModal();
        }
      });
    }
  };


  const checkPolicyMembers = (data: any) => {
    let policiesMembers = 0;

    if(Number(data.policy_type_id) === PolciyTypeEnum.OBAMACARE) {
      if (data.additional_information.members_insured != undefined) {
        if (data.additional_information.members_insured <= 0) {
          policiesMembers++;
        }
      }
    } else {
      policiesMembers++;
    }

    return policiesMembers > 0;
  };

  const closeModal = () => {
    reset();
    setSelectedCommissionRule(undefined);
    setCommissionRules([]);
    setSelectedPossibleClient(null);
    setSelectedClientPolicies([]);
    onClose();
  };

  return (
    <>
      <ToastContainer progressStyle={{ background: "#D4AF37" }} />
      <Modal
        isOpen={info.show}
        onClose={closeModal}
        label={t("REVIEW_AND_MATCH_POLICY")}
        saveButton={{
          label: t("SAVE"),
          icon: <PaperAirplaneIcon />,
          onClick: handleSubmit(onSubmit, onSubmitInvalid),
          disabled: loadingRules || loadingPossibleClient || loadingPolicies 
        }}
      >
        {isPolicyUpdateLoading && <LoadingMask />}
        <div className="w-full flex flex-wrap">
          <Fragment>
            <ReactiveFormInput
              control={control}
              className="md:w-1/3"
              label={t("POLICY_NUMBER")}
              name={`policy_number`}
            />
            {agentAvailableMarkets.length > 0 && (
              <>
                <ReactiveFormSelect
                  width="md:w-1/3"
                  name={`policy_market_id`}
                  label={t("POLICY_MARKET")}
                  options={agentAvailableMarkets.map(
                    (x: AgencyPolicyType) => {
                      return {
                        label: `${x.market_name}`,
                        value: `${x.market_id}`,
                      };
                    }
                  )}
                  control={control}
                  handleOnChange={(x: any) => {
                    methods.resetField("policy_type_id");
                    setSelectedPolicyType(undefined);
                    let aPolicyM = agentAvailableMarkets.find(
                      (market) => market.market_id === +x.target.value
                    );
                    if (aPolicyM !== undefined) {
                      setPolicyTypes(aPolicyM.policy_types!);
                      setSelectedPolicyMarket({
                        id: aPolicyM.market_id,
                        name: aPolicyM.market_name,
                      });
                    } else {
                      setSelectedPolicyMarket(undefined);
                      setPolicyTypes([]);
                    }
                  }}
                  isRequired
                />

                <ReactiveFormSelect
                  width="md:w-1/3"
                  name={`policy_type_id`}
                  label={t("POLICY_TYPE")}
                  options={policyTypes.map((t: PolicyType) => {
                    return { label: `${t.name}`, value: `${t.id}` };
                  })}
                  control={control}
                  isDisabled={
                    methods.getValues("policy_market_id") === undefined || loadingTypes
                  }
                  isRequired
                  handleOnChange={(e: any) => {
                    //methods.resetField("carrier_id");
                    let policyType = policyTypes.find(
                      (x: PolicyType) => x.id === +e.target.value
                    );
                    setObamacareMembersInsured(policyType);
                    setSelectedPolicyType(policyType);
                    /*setLoadingCarriers(true);
                    dispatch(
                      fetchCarriersForPolicyType(+e.target.value)
                    ).then((e) => {
                      setCarriers((e.payload || []) as Carrier[]);
                      setLoadingCarriers(false);
                    });*/
                  }}
                />
              </>)
            }
            <ReactiveFormSelect
              width="md:w-1/3"
              name={`carrier_id`}
              label={t("CARRIER")}
              options={agencyCarriers.map((c) => { return {label: c.value, value: `${c.id}`} })}
              control={control}
              isRequired
              isDisabled
            />
            <ReactiveFormInput
              control={control}
              className="md:w-1/3"
              label={t("STATEMENT_CLIENT_NAME")}
              name={`client`}
              isDisabled={true}
            />
            <ReactiveFormRemoteSearchableSelect
              control={control}
              className="md:w-1/3"
              label={t("CLIENT")}
              name={`client_id`}
              defaultItem={selectedPossibleClient}
              dataSourceAsyncThunk={searchClients}
              data={{ agencyId: currentUser?.agency_id }}
              isDisabled={loadingPossibleClient}
              isRequired 
              onChange={(v: any) => setSelectedPossibleClient(v)}/>

              {loadingPolicies && <div className="md:w-1/3 pl-4 pt-10"><SpinCircle/></div> } 
              {!loadingPolicies && selectedClientPolicies.length > 0 && <ReactiveFormSelect
                className="md:w-1/3"
                name={`policy_id_to_override`}
                label={t("OVERRIDE_EMPTY_POLICY")}
                options={[{
                  label: t("CREATE_NEW_POLICY"), value: "-1"}, 
                  ...selectedClientPolicies.map((x: Policy) => ({label: t("UPDATE_EMPTY_POLICY_MESSAGE", {id: x.id, agent: x.agent!}), value: `${x.id}`}))]}
                control={control}
                isRequired
                handleOnChange={(e: any) => {
                  if (e.target.value !== "-1") {
                    let policy = selectedClientPolicies.find((x: Policy) => x.id === +e.target.value);
                    methods.setValue("agent_id", +policy!.agent_id!);
                    loadCommissionRules(+policy!.agent_id!);
                  } else {
                    if (info.policy?.possible_agent_id && agents.find((x: any) => x.id === info.policy!.possible_agent_id)) {
                      methods.setValue("agent_id", info.policy.possible_agent_id);
                      loadCommissionRules(+info.policy.possible_agent_id!);
                    }
                  }
                }}
              />}

            {/*!loadingPolicies && selectedClientPolicies.length === 0 && <div className="md:w-1/3"/>*/}
            <ReactiveFormInput
              control={control}
              type="date"
              className="md:w-1/3"
              label={t("START_DATE")}
              name={`effective_start_date`}
              isRequired
            />
            <ReactiveFormInput
              control={control}
              type="date"
              className="md:w-1/3"
              label={t("END_DATE")}
              name={`effective_end_date`}
            />
            <ReactiveFormSelect
              width="md:w-1/3"
              name={`policy_state`}
              label={t("STATUS")}
              options={getPolicyStates()}
              control={control}
              isRequired
            />
            <ReactiveFormInput
              control={control}
              type="date"
              className="md:w-1/3"
              label={t("SOLD_DATE")}
              name={`sold_date`}
              isRequired
            />
            <div className="md:w-full"/>
          </Fragment>
          <Fragment>
              <ReactiveFormInput
                className="md:w-1/3"
                name={`statement_agent_name`}
                label={t("STATEMENT_AGENT_NAME")}
                control={control}
                isDisabled
              />
            <ReactiveFormSelect
                width="md:w-1/3"
                name={`agent_id`}
                label={t("AGENT")}
                options={agents.map((x: User) => {
                  return {
                    label: `${x.first_name} ${x.last_name}`,
                    value: `${x.id}`,
                  };
                })}
                control={control}
                handleOnChange={(e: any) => {
                  loadCommissionRules(+e.target.value);
                }}
                isRequired
              />
              <ReactiveFormSelect
                width="md:w-1/3"
                name={`agent_commission_rule_id`}
                label={t("AGENT_COMMISSION_RULE")}
                options={commissionRules.map((r: AgentCommissionRule) => {
                  return {
                    label: `${r.name} - ${
                      r.is_relative ? r.value! * 100 + "%" : "$" + r.value
                    }`,
                    value: `${r.id}`,
                  };
                })}
                control={control}
                isDisabled={
                  loadingRules
                }
                isRequired
                handleOnChange={(e: any) =>
                  setSelectedCommissionRule(
                    commissionRules.find(
                      (x: AgentCommissionRule) => x.id === +e.target.value
                    )
                  )
                }
              />

              {selectedCommissionRule !== undefined && (
                <p className="text-slate-500 text-xs px-3">
                  {selectedCommissionRule.description}
                </p>
              )}
            </Fragment>

            {
              ((selectedPolicyMarket && selectedPolicyMarket.id !== PolciyMarketEnum.LIFE)
              && (selectedPolicyType && selectedPolicyType.id === PolciyTypeEnum.OBAMACARE)) && (
                <>
                  <p className="text-gray-600 font-bold w-full ml-3 mt-5 mb-3">{t("OBAMACARE_INFORMATION")}</p>
                  <Fragment>
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("MEMBERS_INSURED")}
                      name={`additional_information.members_insured`}
                      type="number"
                      isRequired
                    />
                    <ReactiveFormRadioButtonSelect
                      className="md:w-1/3"
                      control={control}
                      name={`additional_information.free_plan`}
                      label={t("FREE_PLAN")}
                      options={[
                        { label: t("YES"), value: "1" },
                        { label: t("NO"), value: "0" },
                      ]}
                    />
                  </Fragment>
                </>
              ) 
            }

            {
              ((selectedPolicyMarket && selectedPolicyMarket.id !== PolciyMarketEnum.LIFE)
              && (selectedPolicyType && selectedPolicyType.id === PolciyTypeEnum.MEDICARE)) && (
                <>
                  <p className="text-gray-600 font-bold w-full ml-3 mt-5 mb-3">{t("MEDICARE_INFORMATION")}</p>
                  <Fragment>
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("MEDICARE_ID")}
                      name={`additional_information.medicare_id`}
                      isRequired
                    />
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("SS_NUMBER")}
                      name={`additional_information.ss_number`}
                      isRequired
                    />
                    <ReactiveFormRadioButtonSelect
                      className="md:w-1/3"
                      control={control}
                      name={`additional_information.scope_of_appointment`}
                      label={t("SCOPE_OF_APPOINTMENT")}
                      isRequired
                      options={[
                        { label: t("YES"), value: "1" },
                        { label: t("NO"), value: "0" },
                      ]}
                    />
                    <ReactiveFormRadioButtonSelect
                      className="md:w-1/3"
                      control={control}
                      name={`additional_information.part_d_plan`}
                      label={t("PART_D_PLAN")}
                      options={[
                        { label: t("YES"), value: "1" },
                        { label: t("NO"), value: "0" },
                      ]}
                    />
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("PLAN_ID")}
                      name={`additional_information.plan_id`}
                      isRequired
                    />
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("SPECIAL_NEED_PLAN")}
                      name={`additional_information.special_need_plan`}
                    />
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("SUPPLEMENT_PLAN")}
                      name={`additional_information.supplement_plan`}
                    />
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("RECORDING_LINK")}
                      name={`additional_information.recording_link`}
                      isRequired
                    />
                  </Fragment>
                  <p className="text-gray-600 font-bold w-full ml-3 mt-5 mb-3">{t("ELIGIBILITY_AND_SEP_TRACKING")}</p>
                  <Fragment>
                    <ReactiveFormInput
                      control={control}
                      type="date"
                      className="md:w-1/3"
                      label={t("ELIGIBILITY_DATE")}
                      name={`additional_information.eligibility_date`}
                      isRequired
                    />
                    <ReactiveFormRadioButtonSelect
                      className="md:w-1/3"
                      control={control}
                      name={`additional_information.dual_eligibility`}
                      label={t("DUAL_ELIGIBILITY")}
                      options={[
                        { label: t("YES"), value: "1" },
                        { label: t("NO"), value: "0" },
                      ]}
                    />
                    <ReactiveFormRadioButtonSelect
                      className="md:w-1/3"
                      control={control}
                      name={`additional_information.low_income_subsidy`}
                      label={t("LOW_INCOME_SUBSIDY")}
                      options={[
                        { label: t("YES"), value: "1" },
                        { label: t("NO"), value: "0" },
                      ]}
                    />
                    <ReactiveFormRadioButtonSelect
                      className="md:w-1/3"
                      control={control}
                      name={`additional_information.sep`}
                      label={t("SPECIAL_ENROLLMENT_PERIOD")}
                      options={[
                        { label: t("YES"), value: "1" },
                        { label: t("NO"), value: "0" },
                      ]}
                    />
                    <ReactiveFormSelect
                      width="md:w-1/3"
                      name="additional_information.enrollment_method"
                      label={t("ENROLLMENT_METHOD")}
                      options={getEnrollmentMethods()}
                      isRequired
                      control={control}
                    />
                  </Fragment>
                  <p className="text-gray-600 font-bold w-full ml-3 mt-5 mb-3">{t("MEDICAL_GENERAL_INFORMATION")}</p>
                  <Fragment>
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("PRIMARY_DOCTOR")}
                      name={`additional_information.primary_doctor`}
                      isRequired
                    />
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("PREFERRED_HOSPITAL")}
                      name={`additional_information.preferred_hospital`}
                      isRequired
                    />
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("SPECIAL_NEEDS")}
                      name={`additional_information.especial_needs`}
                    />
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("PRESCRIPTION_DRUGS")}
                      name={`additional_information.prescription_drugs`}
                      isRequired
                    />
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("PREFERRED_PHARMACY")}
                      name={`additional_information.preferred_pharmacy`}
                      isRequired
                    />
                  </Fragment>
                </>
              ) 
            }
            
            {
              selectedPolicyMarket && selectedPolicyMarket.id === PolciyMarketEnum.LIFE && (
                <>
                  <p className="text-gray-600 font-bold w-full ml-3 mt-5 mb-3">{t("LIFE_INFORMATION")}</p>
                  <Fragment>
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("ANUAL_PREMIUM")}
                      name={`additional_information.anual_premium`}
                      type="number"
                      isRequired
                    />
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("FACE_VALUE")}
                      name={`additional_information.face_value`}
                      type="number"
                      isRequired
                    />
                  </Fragment>
                </>
              )
            }
        </div>
      </Modal>
    </>
  );
};
